import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaUser, FaBars } from 'react-icons/fa';
import JsSIP from 'jssip';
import axios, { getBaseUrl } from '../../services/api';
import ConfirmModal from './ConfirmModal';
import CallModal from './CallModal';
import callcenterLogo from '../../styles/callcenter_logo.png';

const getTimestamp = () => {
  const now = new Date();
  return now.toLocaleTimeString('zh-TW', { hour12: false }) + '.' + String(now.getMilliseconds()).padStart(3, '0');
};

const createTimer = (label) => {
  const start = Date.now();
  return {
    mark: (subLabel) => {
      const elapsed = Date.now() - start;
      console.log(`⏱️ [性能] ${label}${subLabel ? ' - ' + subLabel : ''} | 耗時: ${elapsed}ms`);
    },
    end: (subLabel) => {
      const elapsed = Date.now() - start;
      console.log(`✅ [性能] ${label}${subLabel ? ' - ' + subLabel : ''} | 總耗時: ${elapsed}ms`);
      return elapsed;
    }
  };
};

const CallAgentVisitPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [callCenterData, setCallCenterData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ua, setUa] = useState(null);
  const [status, setStatus] = useState('Not Connected');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [isVideoCall, setIsVideoCall] = useState(false);
  const [enableVideoCall, setEnableVideoCall] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isLocalVideoActive, setIsLocalVideoActive] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState('unregistered');
  const [isSystemReady, setIsSystemReady] = useState(false);

  const sessionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const originalSendRef = useRef(null);
  const iceServersRef = useRef([]);
  const tempPcRef = useRef(null);
  const candidateBufferRef = useRef([]);

  const centerId = searchParams.get('id');

  const checkEnableVideoCall = () => {
    const isEnable = process.env.REACT_APP_ENABLE_VIDEO_CALL || '';
    if (isEnable === 'TRUE') setEnableVideoCall(true);
    else setEnableVideoCall(false);
  };

  const parseIceServers = (serverString) => {
    if (!serverString || typeof serverString !== 'string') return [];
    const servers = serverString.split(',').map(s => s.trim()).filter(Boolean);
    const iceServers = [];
    const turnUsername = process.env.REACT_APP_TURN_USERNAME || '';
    const turnPassword = process.env.REACT_APP_TURN_PASSWORD || '';
    for (const server of servers) {
      if (/^turn:/i.test(server)) {
        iceServers.push({ urls: server, username: turnUsername, credential: turnPassword });
      } else if (/^stun:/i.test(server)) {
        iceServers.push({ urls: server });
      }
    }
    return iceServers;
  };

  useEffect(() => {
    const isMobileDevice = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    };

    const loadVConsole = () => {
      return new Promise((resolve) => {
        if (!isMobileDevice()) {
          console.log('🖥️ [調試] 桌面端瀏覽器，跳過 vConsole 加載 | 時間:' + getTimestamp());
          resolve(null);
          return;
        }

        if (window.vConsoleInstance) {
          resolve(window.vConsoleInstance);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/vconsole@latest/dist/vconsole.min.js';
        script.async = false;
        script.onload = () => {
          window.vConsoleInstance = new window.VConsole({ maxLogNumber: 10000 });
          console.log('📱 [調試] vConsole 加載完成（移動端）| 時間:' + getTimestamp());
          resolve(window.vConsoleInstance);
        };
        script.onerror = () => {
          console.error('❌ [調試] vConsole 腳本加載失敗 | 時間:' + getTimestamp());
          resolve(null);
        };
        document.head.appendChild(script);
      });
    };

    const enableJsSIPDebug = () => {
      const wait = () => {
        if (isMobileDevice() && !window.vConsoleInstance) {
          setTimeout(wait, 100); return;
        }
        localStorage.setItem('debug', 'JsSIP:*');
        if (JsSIP.debug?.enable) JsSIP.debug.enable('JsSIP:*');
        console.log('✅ [調試] JsSIP 調試已啟用 | 時間:' + getTimestamp());
      };
      wait();
    };

    const interceptWS = () => {
      if (window._wsDone) return;
      window._wsDone = true;
      const OriginalWS = window.WebSocket;
      window.WebSocket = function(url, protocols) {
        const ws = new OriginalWS(url, protocols);
        console.log(`[🔌 WS] 連接：${url} | 時間:` + getTimestamp());

        ws.addEventListener('open', () => {
          console.log('✅ [WS] 連接已建立 | 時間:' + getTimestamp());
        });
        ws.addEventListener('close', () => {
          console.log('🔴 [WS] 連接已關閉 | 時間:' + getTimestamp());
        });
        ws.addEventListener('error', (e) => {
          console.log('❌ [WS] 連接錯誤:', e, '| 時間:' + getTimestamp());
        });

        ws.addEventListener('message', e => {
          const recvTime = getTimestamp();
          const d = typeof e.data === 'string' ? (e.data.length > 800 ? e.data.slice(0,800)+'...（省略）' : e.data) : '[Binary]';
          console.log('[📥 WS 接收]', d, '| 時間:', recvTime);

          // 🔵 新增：檢測 401 響應
          if (typeof e.data === 'string' && e.data.includes('401')) {
            console.log('🔐 [SIP] 收到 401 Unauthorized | 時間:', recvTime);
          }
          if (typeof e.data === 'string' && e.data.includes('180')) {
            console.log('📞 [SIP] 收到 180 Ringing | 時間:', recvTime);
          }
        });
        const origSend = ws.send;
        ws.send = function(data) {
          const sendTime = getTimestamp();
          const bufferedAmount = this.bufferedAmount;
          const readyState = this.readyState;
          const msgPreview = typeof data === 'string' ? (data.length > 200 ? data.slice(0,200)+'...' : data) : '[Binary]';
          console.log('[📤 WS 發送]', msgPreview, '| 時間:', sendTime);
          console.log('[📤 WS 發送] 緩衝區大小:', bufferedAmount);
          console.log('[📤 WS 發送] readyState:', readyState);

          // 🔵 新增：檢測 INVITE 消息
          if (typeof data === 'string' && data.includes('INVITE')) {
            console.log('📤 [SIP] 發送 INVITE | 時間:', sendTime);
            const callIdMatch = data.match(/Call-ID:\s*(\S+)/);
            if (callIdMatch) {
              console.log('📤 [SIP] Call-ID:', callIdMatch[1]);
            }
          }

          return origSend.call(this, data);
        };
        return ws;
      };
      console.log('✅ [調試] WebSocket 攔截已啟用 | 時間:' + getTimestamp());
    };

    (async () => {
      console.log('=== 🛠️ 調試模塊初始化開始 | 時間:' + getTimestamp() + ' ===');
      await loadVConsole();
      if (isMobileDevice()) {
        if (window.vConsoleInstance) {
          console.log('✅ [調試] 移動端：vConsole 已就緒 | 時間:' + getTimestamp());
        } else {
          console.log('⚠️ [調試] 移動端：vConsole 加載失敗 | 時間:' + getTimestamp());
        }
      } else {
        console.log('🖥️ [調試] 桌面端：使用原生 Console (F12) | 時間:' + getTimestamp());
      }
      enableJsSIPDebug();
      interceptWS();
      console.log('=== 🛠️ 調試模塊初始化完成 | 時間:' + getTimestamp() + ' ===');
    })();
  }, []);

  const applyCodecPreferences = (pc) => {
    if (!pc || !RTCRtpSender.getCapabilities) return;
    const audioTransceiver = pc.getTransceivers().find(t =>
      (t.sender?.track?.kind === 'audio' || t.receiver?.track?.kind === 'audio')
    );
    if (audioTransceiver) {
      const audioCaps = RTCRtpSender.getCapabilities('audio');
      const pcmuCodec = audioCaps.codecs.find(c => c.mimeType.toLowerCase() === 'audio/pcmu');
      const dtmfCodec = audioCaps.codecs.find(c => c.mimeType.toLowerCase().includes('telephone-event'));
      if (pcmuCodec && dtmfCodec) {
        try { audioTransceiver.setCodecPreferences([pcmuCodec, dtmfCodec]); } catch(e) {console.log(e);}
      }
    }
  };

  const injectSdpPatch = (uaInstance) => {
    if (!originalSendRef.current && uaInstance?.transport) {
      originalSendRef.current = uaInstance.transport.send;
      uaInstance.transport.send = function (...args) {
        if (args[0]?.body) {
          let body = args[0].body;

          const serverIP = process.env.REACT_APP_SIP_SERVER_IP || '35.221.190.216';

          if (body.includes('127.0.0.1') || body.includes('0.0.0.0')) {
            console.log('🔧 [SDP] 檢測到 localhost IP，替換為:', serverIP, '| 時間:', getTimestamp());
            body = body.replace(/IN IP4 (127\.0\.0\.1|0\.0\.0\.0)/g, `IN IP4 ${serverIP}`);
            body = body.replace(/c=IN IP4 (127\.0\.0\.1|0\.0\.0\.0)/g, `c=IN IP4 ${serverIP}`);
            args[0].body = body;
          }
        }
        return originalSendRef.current.apply(this, args);
      };
    }
  };

  const bindRemoteAudio = (pc) => {
    if (!pc) return;
    try {
      const audioReceiver = pc.getReceivers().find(r => r.track && r.track.kind === 'audio');
      if (!audioReceiver?.track) return;
      const audioEl = document.getElementById('remoteAudio');
      if (!audioEl) return;
      const stream = new MediaStream([audioReceiver.track]);
      audioEl.srcObject = stream;
      audioEl.play().catch(() => {});
    } catch (e) { console.error('音頻喚醒失敗:', e); }
  };

  const upgradeToVideo = async () => {
    const session = sessionRef.current;
    if (!session || session.connection.signalingState !== 'stable') {
      setTimeout(upgradeToVideo, 500);
      return;
    }
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, frameRate: 20 }
      });
      const pc = session.connection;
      const videoTrack = videoStream.getVideoTracks()[0];
      if (localStreamRef.current) localStreamRef.current.addTrack(videoTrack);
      pc.addTrack(videoTrack, localStreamRef.current || videoStream);
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
      setIsLocalVideoActive(true);
      applyCodecPreferences(pc);
      console.log('📹 [視頻] 視頻升級完成 | 時間:' + getTimestamp());
      setTimeout(() => {
        session.renegotiate({
          useAudio: true, useVideo: true,
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: true }
        });
      }, 500);
      setTimeout(() => bindRemoteAudio(pc), 3500);
    } catch (e) {
      console.log('❌ [視頻] 視頻升級失敗:', e.message, '| 時間:' + getTimestamp());
      toast.error('無法開啟攝像頭');
    }
  };

  useEffect(() => {
    if (!callCenterData?.webrtcInfo) return;

    const turnServerString = process.env.REACT_APP_STUN_SERVER || '';
    const turnUsername = process.env.REACT_APP_TURN_USERNAME || '';
    const turnPassword = process.env.REACT_APP_TURN_PASSWORD || '';

    console.log({ turnServerString });
    console.log({ turnUsername });
    console.log({ turnPassword });

    console.log('🔌 [SIP] 開始初始化 UA | 時間:' + getTimestamp());
    iceServersRef.current = parseIceServers(process.env.REACT_APP_ICE_SERVERS);
    console.log('🔍 [ICE] parseIceServers 解析結果:', JSON.stringify(iceServersRef.current));

    JsSIP.debug.enable('JsSIP:*');

    const socket = new JsSIP.WebSocketInterface(
      `wss://${callCenterData?.webrtcInfo?.domain ?? 'im.qrtalkie.com'}:8089/ws`
    );

    const uaInstance = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${callCenterData.webrtcAccount}@${callCenterData?.webrtcInfo?.domain ?? 'im.qrtalkie.com'}`,
      password: callCenterData?.webrtcInfo?.password || '12345678',
      register: true
    });


    uaInstance.on('registered', () => {
      console.log('✅ [SIP] 註冊成功 | UA:', uaInstance.configuration.uri, '| 時間:' + getTimestamp());
      setRegistrationStatus('registered');
      injectSdpPatch(uaInstance);
      setStatus(t('Ready'));
    });

    uaInstance.on('unregistered', () => {
      console.log('❌ [SIP] 註銷 | 時間:' + getTimestamp());
      setRegistrationStatus('unregistered');
      setStatus(t('Disconnected'));
    });

    uaInstance.on('registrationFailed', () => {
      console.log('❌ [SIP] 註冊失敗 | 時間:' + getTimestamp());
      setRegistrationStatus('unregistered');
      setStatus(t('Registration Failed'));
    });

    uaInstance.start();
    setUa(uaInstance);
    return () => {
      console.log('🛑 [SIP] UA 停止 | 時間:' + getTimestamp());
      uaInstance.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callCenterData]);

  useEffect(() => {
    const fetchCallCenterData = async () => {
      try {
        if (!centerId) {
          toast.error(t('Invalid URL parameters'));
          return;
        }
        const response = await axios.get(`/callcenters/center-id/${centerId}`);
        setCallCenterData(response.data);
      } catch (error) {
        console.error('Error fetching call center data:', error);
        toast.error(t('Failed to load call center data'));
      }
    };

    fetchCallCenterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        if (callCenterData?._id) {
          const response = await axios.get(`/callcenter-categories/center/${callCenterData._id}`);
          setCategories(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callCenterData]);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, selectedCategory]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        if (selectedCategory?._id) {
          const response = await axios.get(`/callcenter-agents/category/${selectedCategory._id}`);
          const publicAgents = (response.data || []).filter(
            agent => agent.publicStatus === 'public'
          );
          setAgents(publicAgents);
          setFilteredAgents(publicAgents);
        } else {
          setAgents([]);
          setFilteredAgents([]);
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAgents([]);
        setFilteredAgents([]);
      }
    };

    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAgents(agents);
    } else {
      const filtered = agents.filter(
        agent =>
          agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.duty?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          agent.sipAccount?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAgents(filtered);
    }
  }, [searchTerm, agents]);

  const handleCategoryClick = category => {
    setSelectedCategory(category);
  };

  const handleHangup = () => {
    console.log('📞 [通話] 執行掛斷 | 時間:' + getTimestamp());

    // 🔵 新增：清理 ICE ready 定時器（防止內存洩漏）
    if (window._iceReadyTimer) {
      clearTimeout(window._iceReadyTimer);
      window._iceReadyTimer = null;
      console.log('🧹 [ICE] 清理 iceReadyTimer');
    }

    if (sessionRef.current?.connection) {
      const pc = sessionRef.current.connection;
      if (pc._keyFrameInterval) {
        clearInterval(pc._keyFrameInterval);
        pc._keyFrameInterval = null;
        console.log('✅ [KeyFrame] Key frame interval cleared');
      }
    }

    localStreamRef.current?.getTracks().forEach(track => track.stop());
    if (tempPcRef.current) { tempPcRef.current.close(); tempPcRef.current = null; }
    if (sessionRef.current) { try { sessionRef.current.terminate(); } catch (e) {console.log(e);} }
    setIsCallModalOpen(false);
    setIsConfirmOpen(false);
    setIsLocalVideoActive(false);
    sessionRef.current = null;
    candidateBufferRef.current = [];
    console.log('✅ [通話] 掛斷完成 | 時間:' + getTimestamp());
  };

  const handleToggleVideo = () => {
    const videoTrack = localStreamRef.current?.getVideoTracks()[0];
    if (videoTrack) {
      const newState = !videoTrack.enabled;
      videoTrack.enabled = newState;
      setIsLocalVideoActive(newState);
      console.log('📹 [視頻] 切換狀態:', newState ? '開啟' : '關閉', '| 時間:' + getTimestamp());
    }
  };

  const handleCallClick = async (agent, video = false) => {
    console.log('=== 🔵 [UI] 用戶點擊通話按鈕 ===');
    console.log('🕐 時間:', getTimestamp());
    console.log('📹 通話類型:', video ? '視頻' : '語音');
    console.log('📊 SIP 狀態:', registrationStatus);

    console.log('🔍 [ICE] ICE 服務器配置:', JSON.stringify(iceServersRef.current));
    console.log('🔍 [ICE] 環境變數 REACT_APP_ICE_SERVERS:', process.env.REACT_APP_ICE_SERVERS);
    console.log('🔍 [ICE] 環境變數 REACT_APP_TURN_USERNAME:', process.env.REACT_APP_TURN_USERNAME ? '已設置' : '未設置');
    console.log('🔍 [ICE] 環境變數 REACT_APP_TURN_PASSWORD:', process.env.REACT_APP_TURN_PASSWORD ? '已設置' : '未設置');
    if (iceServersRef.current.length === 0) {
      console.warn('⚠️ [ICE] 警告：ICE 服務器列表為空，請檢查環境變數 REACT_APP_ICE_SERVERS 配置');
    } else {
      console.log('✅ [ICE] ICE 服務器列表正常，共', iceServersRef.current.length, '個服務器');
    }

    if (!ua || registrationStatus !== 'registered') {
      console.log('❌ [UI] 失敗：SIP 未註冊 | 時間:' + getTimestamp());
      return toast.error('SIP 未就緒');
    }

    setSelectedAgent(agent);
    setIsVideoCall(video);
    setIsSystemReady(false);
    candidateBufferRef.current = [];
    console.log('🔄 [狀態] 初始化完成 | 時間:' + getTimestamp());

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') ctx.resume();
      console.log('🎵 [音頻] AudioContext 已解鎖 | 時間:' + getTimestamp());
    } catch (e) {
      console.log(e);
    }

    try {
      setIsConfirmOpen(true);
      console.log('📋 [UI] 顯示確認框 | 時間:' + getTimestamp());

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      console.log('✅ [媒體] 麥克風權限已授予 | 時間:' + getTimestamp());

      const tempPc = new RTCPeerConnection({ iceServers: iceServersRef.current });
      tempPcRef.current = tempPc;
      stream.getTracks().forEach(track => tempPc.addTrack(track, stream));
      console.log('🔌 [ICE] 臨時 PeerConnection 已創建 | 時間:' + getTimestamp());

      let hasRelay = false;
      const hardTimeout = setTimeout(() => {
        if (!isSystemReady) {
          console.warn('⚠️ [ICE] 硬性超時，強制開鎖 | 時間:' + getTimestamp());
          setIsSystemReady(true);
        }
      }, 3000);

      tempPc.onicecandidate = (event) => {
        if (event.candidate) {
          candidateBufferRef.current.push(event.candidate);
          console.log('🔍 [ICE] 探測到路徑:', event.candidate.candidate.substring(0, 100) + '... | 時間:' + getTimestamp());
          if (event.candidate.candidate.includes('typ relay') && !hasRelay) {
            hasRelay = true;
            console.log('🟢 [ICE] 成功獲取 Relay 路徑，系統就緒 | 時間:' + getTimestamp());
            clearTimeout(hardTimeout);
            setIsSystemReady(true);
          }
        }
      };

      const offer = await tempPc.createOffer();
      await tempPc.setLocalDescription(offer);
      console.log('📝 [ICE] Offer 已創建並設置 | 時間:' + getTimestamp());

    } catch (e) {
      console.log('❌ [媒體] 權限獲取失敗:', e.message, '| 時間:' + getTimestamp());
      toast.error('請授權麥克風');
      setIsConfirmOpen(false);
    }
  };

  // const handleCallClick = async (agent, isVideo = false) => {
  //   if (!ua) {
  //     toast.error(t('Please wait for connection to be established'));
  //     return;
  //   }

  //   const permissionGranted = await requestMediaPermissions(isVideo);
  //   if (!permissionGranted) {
  //     return;
  //   }

  //   setSelectedAgent(agent);
  //   setIsVideoCall(isVideo);
  //   setIsConfirmOpen(true);
  // };

  const handleConfirmCall = () => {
    const callTimer = createTimer('handleConfirmCall');
    const callStartTime = Date.now();

    console.log('=== 🔴 [UI] 用戶點擊確認呼叫 ===');
    console.log('🕐 時間:', getTimestamp());
    console.log('⏱️ [性能] 時間戳:', callStartTime);

    console.log('🔍 [DEBUG] ========== selectedAgent 診斷 ==========');
    console.log('🔍 [DEBUG] selectedAgent:', selectedAgent);
    console.log('🔍 [DEBUG] selectedAgent.sipAccount:', selectedAgent?.sipAccount);
    console.log('🔍 [DEBUG] selectedAgent.sipAccount 類型:', typeof selectedAgent?.sipAccount);
    console.log('🔍 [DEBUG] selectedAgent.sipAccount 長度:', selectedAgent?.sipAccount?.length);

    const targetUri = `sip:${selectedAgent?.sipAccount}@${selectedAgent?.sipAccountInfo?.domain ?? 'im.qrtalkie.com'}`;
    console.log('🔍 [DEBUG] 構建的目標 URI:', targetUri);

    const uriRegex = /^sip:[^@]+@[^@]+$/;
    console.log('🔍 [DEBUG] URI 格式驗證:', uriRegex.test(targetUri) ? '✅ 正確' : '❌ 錯誤');
    console.log('🔍 [DEBUG] ========== 診斷結束 ==========');

    console.log('📞 被叫號碼:', targetUri);
    console.log('📹 通話類型:', isVideoCall ? '視頻' : '語音');

    console.log('🔍 [DEBUG] isSystemReady:', isSystemReady);
    console.log('🔍 [DEBUG] candidateBufferRef 長度:', candidateBufferRef.current.length);
    console.log('🔍 [DEBUG] ua 狀態:', ua ? '已初始化' : '未初始化');
    console.log('🔍 [DEBUG] selectedAgent:', selectedAgent ? '已加載' : '未加載');
    console.log('🔍 [DEBUG] localStream:', localStreamRef.current ? '已獲取' : '未獲取');
    callTimer.mark('狀態檢查完成');

    const transport = ua?.transport;
    const socket = transport?.socket;
    console.log('🔌 [DEBUG] WebSocket readyState:', socket?.readyState);
    console.log('🔌 [DEBUG] WebSocket URL:', socket?.url);
    console.log('🔌 [DEBUG] transport 狀態:', transport?.connected);
    console.log('🔌 [DEBUG] WebSocket bufferedAmount:', socket?.bufferedAmount);
    callTimer.mark('WebSocket 狀態檢查');

    setIsConfirmOpen(false);
    setIsCallModalOpen(true);
    setCallStatus('呼叫中...');

    const audioEl = document.getElementById('remoteAudio');
    if (audioEl) audioEl.play().catch(() => {});

    console.log('=== 📤 [SIP] 即將發送 INVITE 請求 ===');
    console.log('🕐 時間:', getTimestamp());
    callTimer.mark('UI 狀態更新完成');

    const callerAccount = `sip:${callCenterData?.webrtcAccount}@${callCenterData?.webrtcAccountData?.domain ?? callCenterData?.webrtcInfo?.domain ?? 'im.qrtalkie.com'}`;

    const options = {
      mediaStream: localStreamRef.current,
      pcConfig: {
        iceServers: iceServersRef.current,
        iceTransportPolicy: 'all',
        iceCandidatePoolSize: 0
      },
      extraHeaders: [
        `P-Asserted-Identity: <${callerAccount}>`,
        `From: "${callCenterData?.webrtcAccount}" <${callerAccount}>`,
        `X-CallerID: ${callCenterData?.webrtcAccount}`,
      ],
      fromDisplayName: callCenterData?.webrtcAccount || 'Call Center',
      eventHandlers: {
        icecandidate: (data) => {
          const { candidate, ready } = data;

          if (candidate) {
            console.log(`🔌 [ICE] candidate: ${candidate.type} | ${candidate.candidate?.substring(0, 100)}... | 時間: ${getTimestamp()}`);
          }

          if (candidate?.candidate?.includes('typ relay')) {
            console.log('✅ [ICE] relay 候選已收集，調用 ready() 立即發送 INVITE | 時間:', getTimestamp());
            ready();
            return;
          }

          if (!window._iceReadyTimer) {
            window._iceReadyTimer = setTimeout(() => {
              console.log('⏰ [ICE] 1 秒超時，強制調用 ready() 發送 INVITE | 時間:', getTimestamp());
              if (ready) ready();
              window._iceReadyTimer = null;
            }, 1000);
          }
        },

        connecting: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('📤 [SIP] connecting 事件觸發 | 耗時:', elapsed, 'ms');
          console.log('📤 [SIP] 正在發送 INVITE | 耗時:', elapsed, 'ms');
          console.log('📞 [SIP] 請求 URI (to_uri):', data.request?.to_uri?.toString());
          console.log('📞 [SIP] 請求 URI (ruri):', data.request?.ruri?.toString());
          console.log('📞 [SIP] Call-ID:', data.request?.call_id);
          console.log('🔍 [SIP] request 對象:', data.request);
          callTimer.mark('connecting 事件觸發');
        },

        progress: (data) => {
          const elapsed = Date.now() - callStartTime;
          const response = data.response;
          console.log('📥 [SIP] 收到響應:', response.status_code, response.reason_phrase, '| 耗時:', elapsed, 'ms');
          console.log('📥 [SIP] From Tag:', response.from_tag);
          console.log('📥 [SIP] To Tag:', response.to_tag);
          console.log('📥 [SIP] Call-ID:', response.call_id);

          if (response.body) {
            console.log('📥 [SIP] 包含 SDP:', response.body.length, 'bytes');
          }

          setCallStatus('正在響鈴...');
          callTimer.mark('progress 事件觸發');

          const pc = sessionRef.current?.connection;
          if (pc && candidateBufferRef.current.length > 0) {
            console.log('🔌 [ICE] 注入緩存候選 | 數量:', candidateBufferRef.current.length);
            candidateBufferRef.current.forEach(cand => {
              pc.addIceCandidate(cand).catch(err => {
                if (err.name !== 'InvalidStateError') {
                  console.log('⚠️ [ICE] 注入候選失敗:', err.message);
                }
              });
            });
            candidateBufferRef.current = [];
          }
        },

        ringing: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('📥 [SIP] 收到 180 Ringing | 耗時:', elapsed, 'ms');
          console.log('📥 [SIP] From Tag:', data.response?.from_tag);
          console.log('📥 [SIP] To Tag:', data.response?.to_tag);
          setCallStatus('正在響鈴...');
          callTimer.mark('ringing 事件觸發');
        },

        peerconnection: (e) => {
          const elapsed = Date.now() - callStartTime;
          console.log('🔌 [Event] peerconnection | ICE 開始協商 | 耗時:', elapsed, 'ms');
          callTimer.mark('peerconnection 事件觸發');

          const pc = e.peerconnection;

          console.log('🔍 [ICE] PC 初始狀態:');
          console.log('  - signalingState:', pc.signalingState);
          console.log('  - iceGatheringState:', pc.iceGatheringState);
          console.log('  - iceConnectionState:', pc.iceConnectionState);
          console.log('  - localDescription:', pc.localDescription ? '已設置' : '未設置');
          console.log('  - remoteDescription:', pc.remoteDescription ? '已設置' : '未設置');

          applyCodecPreferences(pc);

          const setupKeyFrameHandling = () => {
            try {
              const senders = pc.getSenders();
              const videoSenders = senders.filter(s => s.track && s.track.kind === 'video');

              if (videoSenders.length === 0) {
                console.log('⚠️ [KeyFrame] No video senders found, will retry...');
                setTimeout(setupKeyFrameHandling, 1000);
                return;
              }

              console.log(`✅ [KeyFrame] Found ${videoSenders.length} video sender(s)`);

              const requestKeyFrame = async () => {
                try {
                  for (const sender of videoSenders) {
                    try {
                      const params = await sender.getParameters();
                      if (!params.encodings || params.encodings.length === 0) {
                        params.encodings = [{}];
                      }

                      params.encodings.forEach(encoding => {
                        encoding.scaleResolutionDownBy = encoding.scaleResolutionDownBy === 1.0 ? 1.01 : 1.0;
                      });

                      await sender.setParameters(params);
                      // console.log('🔄 [KeyFrame] Key frame requested via setParameters');

                      setTimeout(async () => {
                        try {
                          const resetParams = await sender.getParameters();
                          resetParams.encodings.forEach(encoding => {
                            encoding.scaleResolutionDownBy = 1.0;
                          });
                          await sender.setParameters(resetParams);
                        } catch (e) {
                          // Ignore reset errors
                        }
                      }, 100);

                    } catch (err) {
                      console.warn('⚠️ [KeyFrame] Failed to request key frame:', err);
                    }
                  }
                } catch (err) {
                  console.error('❌ [KeyFrame] Error requesting key frame:', err);
                }
              };

              const keyFrameInterval = setInterval(() => {
                if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
                  clearInterval(keyFrameInterval);
                  return;
                }

                requestKeyFrame();
              }, 2000);

              pc._keyFrameInterval = keyFrameInterval;

              console.log('✅ [KeyFrame] Periodic key frame requests enabled (every 2s)');

            } catch (err) {
              console.error('❌ [KeyFrame] Error setting up key frame handling:', err);
            }
          };

          pc.onicegatheringstatechange = () => {
            console.log('🔍 [ICE] gatheringState 變更:', pc.iceGatheringState, '| 時間:', getTimestamp());
            if (pc.iceGatheringState === 'complete') {
              console.log('✅ [ICE] ICE 收集完成');
            }
          };

          pc.oniceconnectionstatechange = () => {
            console.log('🔌 [ICE] 狀態變更:', pc.iceConnectionState, '| 時間:', getTimestamp());
            if (pc.iceConnectionState === 'connected') {
              console.log('✅ [ICE] 連接已建立');
              bindRemoteAudio(pc);
              setTimeout(setupKeyFrameHandling, 1000);
            }
          };

          if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
            setTimeout(setupKeyFrameHandling, 1000);
          }

          pc.onicecandidate = (event) => {
            if (event.candidate) {
              console.log('🔍 [ICE] 正式 PC 收集到候選:', event.candidate.type, '| 時間:', getTimestamp());
            } else {
              console.log('✅ [ICE] 正式 PC 候選收集結束 | 時間:', getTimestamp());
            }
          };

          pc.ontrack = (event) => {
            console.log('🎵 [Event] 收到軌道 | 類型:', event.track.kind, '| 時間:', getTimestamp());
            if (event.track.kind === 'video' && remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = new MediaStream([event.track]);
            }
          };
        },

        accepted: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('✅ [SIP] 收到 200 OK | 耗時:', elapsed, 'ms');
          console.log('✅ [SIP] From Tag:', data.response?.from_tag);
          console.log('✅ [SIP] To Tag:', data.response?.to_tag);
          console.log('✅ [SIP] Contact:', data.response?.contact?.[0]?.uri?.toString());

          if (data.response?.body) {
            console.log('✅ [SIP] SDP 長度:', data.response.body.length, 'bytes');
          }
          callTimer.mark('accepted 事件觸發');
        },

        confirmed: (_) => {
          const elapsed = Date.now() - callStartTime;
          console.log('✅ [Event] confirmed | 通話已建立 | 總耗時:', elapsed, 'ms');
          setCallStatus('通話中');
          callTimer.mark('confirmed 事件觸發');

          const pc = sessionRef.current?.connection;
          if (pc && candidateBufferRef.current.length > 0) {
            console.log('🔌 [ICE] 注入剩餘候選 | 數量:', candidateBufferRef.current.length);
            candidateBufferRef.current.forEach(cand => {
              pc.addIceCandidate(cand).catch(err => {
                if (err.name !== 'InvalidStateError') {
                  console.log('⚠️ [ICE] 注入候選失敗:', err.message);
                }
              });
            });
            candidateBufferRef.current = [];
          }

          if (isVideoCall) setTimeout(upgradeToVideo, 4500);
        },

        failed: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('❌ [SIP] 呼叫失敗 | 耗時:', elapsed, 'ms');
          console.log('❌ [SIP] 失敗原因:', data.cause?.code, data.cause?.reason);
          console.log('❌ [SIP] 失敗詳情:', data);

          if (data.response) {
            console.log('❌ [SIP] 響應碼:', data.response.status_code, data.response.reason_phrase);
          }

          toast.error('呼叫失敗: ' + (data.cause?.reason || '未知錯誤'));
          handleHangup();
          callTimer.mark('failed 事件觸發');
        },

        ended: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('📞 [Event] ended | 通話結束 | 總時長:', elapsed, 'ms');
          console.log('📞 [SIP] 結束原因:', data.cause?.code, data.cause?.reason);
          handleHangup();
          callTimer.mark('ended 事件觸發');
        },

        newRTCSession: (data) => {
          console.log('📞 [SIP] 新 RTC 會話 | 方向:', data.originator);
          console.log('📞 [SIP] 會話 ID:', data.session?.id);
          console.log('📞 [SIP] session 對象:', data.session);
          callTimer.mark('newRTCSession 事件觸發');
        }
      }
    };

    console.log('📤 [SIP] 執行 ua.call() 前 | 時間:', getTimestamp());
    callTimer.mark('準備調用 ua.call()');

    console.log('🔍 [DEBUG] ua.call() 參數診斷:');
    console.log('  - target URI:', targetUri);
    console.log('  - options.mediaStream:', options.mediaStream ? '已設置' : '未設置');
    console.log('  - options.pcConfig.iceServers:', options.pcConfig.iceServers);
    console.log('  - options.pcConfig.iceCandidatePoolSize:', options.pcConfig.iceCandidatePoolSize);

    const callExecStart = Date.now();
    console.log('⏱️ [DEBUG] ua.call() 開始執行');

    try {
      sessionRef.current = ua.call(targetUri, options);
      const callExecEnd = Date.now();
      console.log('✅ [SIP] ua.call() 執行完成 | 耗時:', callExecEnd - callExecStart, 'ms');
      console.log('✅ [SIP] sessionRef.current:', sessionRef.current ? '已創建' : 'null');
      console.log('✅ [SIP] session 對象:', sessionRef.current);

      if (sessionRef.current) {
        console.log('🔍 [SIP] session.connection:', sessionRef.current.connection);
        console.log('🔍 [SIP] session.request:', sessionRef.current.request);
      }
    } catch (error) {
      console.error('❌ [SIP] ua.call() 執行異常:', error);
      console.error('❌ [SIP] 錯誤堆棧:', error.stack);
    }

    callTimer.mark('ua.call() 執行完成');

    if (tempPcRef.current) {
      tempPcRef.current.close();
      tempPcRef.current = null;
      console.log('🔌 [ICE] 臨時 PC 已關閉');
    }

    console.log('✅ [SIP] INVITE 已發送 | 時間:', getTimestamp());
    callTimer.end('handleConfirmCall 總耗時');
  };

  useEffect(() => {
    checkEnableVideoCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="flex flex-col">
        <div
          className="h-[50px] md:h-[70px] flex items-center justify-between px-2 md:px-8 shadow-md"
          style={{ backgroundColor: '#1E2128' }}
        >
          <div className="flex items-center space-x-2 md:space-x-3">
            <img src={callcenterLogo} className="w-8 h-8 md:w-12 md:h-12" alt="logo" />
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-bold text-white">
                {callCenterData?.name || 'Call Center'}
              </span>
            </div>
          </div>

          <div className="hidden md:flex-1 max-w-xl mx-12">
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('Search agents...')}
                className="w-full pl-12 pr-4 py-2 bg-gray-50 text-gray-800 border text-sm border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
              />
            </div>
          </div>

          <div
            className="flex items-center space-x-1 md:space-x-2 px-2 md:px-4 py-1 md:py-2 rounded-lg"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <div
              className={`w-3 h-3 md:w-4 md:h-4 rounded-full ${
                status === t('Ready')
                  ? 'bg-green-500'
                  : status === t('Connecting...')
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
              }`}
            />
            <span
              className={`text-xs md:text-sm font-medium ${
                status === t('Ready')
                  ? 'text-green-400'
                  : status === t('Connecting...')
                    ? 'text-yellow-400'
                    : 'text-red-400'
              }`}
            >
              {status}
            </span>
          </div>
        </div>

        <div className="md:hidden px-3 py-2 border-b" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium" style={{ color: '#6c757d' }}>
              {t('Department')}:{' '}
              <span className="text-sm font-semibold text-gray-800">
                {selectedCategory?.name || t('All')}
              </span>
            </p>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600 p-1"
              aria-label="Menu"
            >
              <FaBars className="text-lg" />
            </button>
          </div>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('Search agents...')}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 text-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {isMobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div
              className="w-64 h-full overflow-y-auto bg-white"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4">
                <h2 className="text-sm font-bold tracking-wide mb-3" style={{ color: '#1E2128' }}>
                  {t('Department')}
                </h2>
                <div className="space-y-1">
                  {categories.map(category => {
                    const isSelected = selectedCategory?._id === category._id;
                    return (
                      <div
                        key={category._id}
                        onClick={() => {
                          handleCategoryClick(category);
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 px-4 py-2 rounded-lg cursor-pointer transition-all duration-150"
                        style={{
                          backgroundColor: isSelected ? '#2563EB' : 'transparent',
                        }}
                      >
                        <span
                          className="text-sm font-medium"
                          style={{ color: isSelected ? '#ffffff' : '#1E2128' }}
                        >
                          {category.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="hidden md:block w-72 overflow-y-auto border-r border-gray-200">
          <div className="p-6">
            <h2 className="text-md font-bold tracking-wide mb-4" style={{ color: '#1E2128' }}>
              {t('Department')}
            </h2>
            <div className="space-y-1">
              {categories.map(category => {
                const isSelected = selectedCategory?._id === category._id;
                return (
                  <div
                    key={category._id}
                    onClick={() => handleCategoryClick(category)}
                    className="flex items-center space-x-3 px-6 py-2 rounded-lg cursor-pointer transition-all duration-150"
                    style={{
                      backgroundColor: isSelected ? '#2563EB' : 'transparent',
                      // borderLeft: isSelected ? '4px solid #424242' : '4px solid transparent',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#2563eb4a';
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* <IconComponent
                      className="text-lg"
                      style={{ color: isSelected ? '#424242' : '#666666' }}
                    /> */}
                    <span
                      className="font-medium"
                      style={{ color: isSelected ? '#ffffff' : '#1E2128' }}
                    >
                      {category.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="px-6 md:px-8 py-2 md:py-6">
            <div className="mb-2 md:mb-6">
              <h2 className="text-sm md:text-2xl font-bold" style={{ color: '#1E2128' }}>
                {/* {selectedCategory?.name || t('Select a category')} */}
                {t('Agent List')}
              </h2>
              <p className="text-sm mt-1" style={{ color: '#6c757d' }}>
                {filteredAgents.length} {filteredAgents.length === 1 ? t('Agent') : t('Agent')}
              </p>
            </div>

            {filteredAgents.length === 0 ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  {/* <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: '#e9ecef' }}
                  >
                    <FaUser className="text-2xl" style={{ color: '#adb5bd' }} />
                  </div> */}
                  <p className="text-sm" style={{ color: '#6c757d' }}>
                    {t('No agents available')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 md:gap-5">
                {filteredAgents.map(agent => {
                  return (
                    <div
                      key={agent._id}
                      className="w-full md:w-[280px] md:max-w-[280px] md:min-w-[280px] rounded-lg md:rounded-xl transition-all duration-200 overflow-hidden bg-white flex-shrink"
                      style={{
                        border: '1px solid #fdede3',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-4px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="px-5 py-3 md:p-5">
                        <div className="hidden md:flex items-start justify-between mb-1.5">
                          <div className="flex-1 flex flex-col items-center">
                            <div className="relative mb-1">
                              <div
                                className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden bg-gray-200"
                                style={{
                                  background: 'linear-gradient(135deg, #e3f2fd 0%, #90caf9 100%)',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                                }}
                              >
                                {agent.avatar ? (
                                  <img
                                    src={
                                      agent.avatar.startsWith('http') ||
                                      agent.avatar.startsWith('data:')
                                        ? agent.avatar
                                        : `${getBaseUrl()}${agent.avatar}`
                                    }
                                    alt={agent.name}
                                    className="w-full h-full object-cover"
                                    onError={e => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML =
                                        '<svg width="36" height="36" fill="#42a5f5" viewBox="0 0 448 512"><path d="M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg>';
                                    }}
                                  />
                                ) : (
                                  <FaUser className="text-2xl" style={{ color: '#42a5f5' }} />
                                )}
                              </div>
                            </div>
                            <div
                              className="text-lg font-semibold text-center"
                              style={{ color: '#212529' }}
                            >
                              {agent.name}
                            </div>
                            <div className="text-lg text-center" style={{ color: '#495057' }}>
                              {agent.duty}
                            </div>
                          </div>
                        </div>

                        <div className="md:hidden flex items-center space-x-4 mb-3">
                          <div className="relative flex-shrink-0">
                            <div
                              className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden bg-gray-200"
                              style={{
                                background: 'linear-gradient(135deg, #e3f2fd 0%, #90caf9 100%)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                              }}
                            >
                              {agent.avatar ? (
                                <img
                                  src={
                                    agent.avatar.startsWith('http') ||
                                    agent.avatar.startsWith('data:')
                                      ? agent.avatar
                                      : `${getBaseUrl()}${agent.avatar}`
                                  }
                                  alt={agent.name}
                                  className="w-full h-full object-cover"
                                  onError={e => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML =
                                      '<svg width="36" height="36" fill="#42a5f5" viewBox="0 0 448 512"><path d="M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg>';
                                  }}
                                />
                              ) : (
                                <FaUser className="text-2xl" style={{ color: '#42a5f5' }} />
                              )}
                            </div>
                          </div>
                          <div className="flex-1 flex flex-col">
                            <div className="text-lg font-semibold" style={{ color: '#212529' }}>
                              {agent.name}
                            </div>
                            <div className="text-base" style={{ color: '#495057' }}>
                              {agent.duty}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2.5">
                          <div className="flex items-center space-x-2">
                            {/* <FaPhone className="text-sm flex-shrink-0" style={{ color: '#6c757d' }} /> */}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 28 28"
                            >
                              <path
                                fill="#6c757d"
                                d="m7.92 2.645l1.66-.5a3.25 3.25 0 0 1 3.903 1.779l1.033 2.298a3.25 3.25 0 0 1-.748 3.71l-1.805 1.683a.3.3 0 0 0-.054.073c-.189.386.098 1.417.997 2.975c1.014 1.756 1.797 2.45 2.16 2.343l2.369-.725a3.25 3.25 0 0 1 3.585 1.207l1.468 2.033a3.25 3.25 0 0 1-.4 4.262l-1.263 1.195a3.75 3.75 0 0 1-3.342.949c-3.517-.732-6.668-3.564-9.48-8.434C5.19 12.62 4.313 8.47 5.443 5.057a3.75 3.75 0 0 1 2.478-2.412m.434 1.436a2.25 2.25 0 0 0-1.487 1.447c-.974 2.941-.185 6.677 2.435 11.215c2.618 4.535 5.456 7.085 8.487 7.715a2.25 2.25 0 0 0 2.005-.57l1.262-1.194a1.75 1.75 0 0 0 .216-2.295l-1.468-2.034a1.75 1.75 0 0 0-1.93-.65l-2.375.727c-1.314.391-2.55-.704-3.892-3.03c-1.137-1.968-1.531-3.39-1.045-4.383q.142-.29.378-.511l1.805-1.683a1.75 1.75 0 0 0 .403-1.998L12.115 4.54a1.75 1.75 0 0 0-2.102-.958z"
                              />
                            </svg>
                            <div className="text-sm truncate" style={{ color: '#495057' }}>
                              {agent?.sipAccountInfo
                                ? `${agent?.sipAccount}@${agent?.sipAccountInfo?.domain ?? 'im.qrtalkie.com'}`
                                : '--'}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {/* <FaEnvelope
                            className="text-sm flex-shrink-0"
                            style={{ color: '#6c757d' }}
                          /> */}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 32 32"
                            >
                              <path
                                fill="#6c757d"
                                d="M6.5 5A4.5 4.5 0 0 0 2 9.5v13A4.5 4.5 0 0 0 6.5 27h19a4.5 4.5 0 0 0 4.5-4.5v-13A4.5 4.5 0 0 0 25.5 5zM28 10.403l-12 6.461l-12-6.461V9.5A2.5 2.5 0 0 1 6.5 7h19A2.5 2.5 0 0 1 28 9.5zM4 12.674l11.526 6.207a1 1 0 0 0 .948 0L28 12.674V22.5a2.5 2.5 0 0 1-2.5 2.5h-19A2.5 2.5 0 0 1 4 22.5z"
                              />
                            </svg>
                            <div className="text-sm truncate" style={{ color: '#495057' }}>
                              {agent?.email || '--'}
                            </div>
                          </div>
                        </div>
                        <div className="hidden md:flex justify-center space-x-4 mt-6">
                          <button
                            onClick={() => handleCallClick(agent, false)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150"
                            style={{
                              backgroundColor: '#DBEAFE',
                              color: '#1E40AF',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = '#1E40AF';
                              e.currentTarget.style.color = '#ffffff';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(30, 136, 229, 0.3)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = '#DBEAFE';
                              e.currentTarget.style.color = '#1E40AF';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            title={t('Voice Call')}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M16.57 22a2 2 0 0 0 1.43-.59l2.71-2.71a1 1 0 0 0 0-1.41l-4-4a1 1 0 0 0-1.41 0l-1.6 1.59a7.55 7.55 0 0 1-3-1.59a7.6 7.6 0 0 1-1.59-3l1.59-1.6a1 1 0 0 0 0-1.41l-4-4a1 1 0 0 0-1.41 0L2.59 6A2 2 0 0 0 2 7.43A15.28 15.28 0 0 0 6.3 17.7A15.28 15.28 0 0 0 16.57 22M6 5.41L8.59 8L7.3 9.29a1 1 0 0 0-.3.91a10.1 10.1 0 0 0 2.3 4.5a10.1 10.1 0 0 0 4.5 2.3a1 1 0 0 0 .91-.27L16 15.41L18.59 18l-2 2a13.28 13.28 0 0 1-8.87-3.71A13.28 13.28 0 0 1 4 7.41zM20 11h2a8.81 8.81 0 0 0-9-9v2a6.77 6.77 0 0 1 7 7"
                              />
                              <path
                                fill="currentColor"
                                d="M13 8c2.1 0 3 .9 3 3h2c0-3.22-1.78-5-5-5z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => enableVideoCall && handleCallClick(agent, true)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150"
                            style={{
                              backgroundColor: '#DCFCE7',
                              color: enableVideoCall ? '#4caf50' : '#c0ccc4',
                            }}
                            disabled={!enableVideoCall}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = '#4caf50';
                              e.currentTarget.style.color = '#ffffff';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(76, 175, 80, 0.3)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = '#DCFCE7';
                              e.currentTarget.style.color = enableVideoCall ? '#4caf50' : '#c0ccc4';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                            title={t('Video Call')}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="currentColor"
                                d="M21.53 7.15a1 1 0 0 0-1 0L17 8.89A3 3 0 0 0 14 6H5a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h9a3 3 0 0 0 3-2.89l3.56 1.78A1 1 0 0 0 21 17a1 1 0 0 0 .53-.15A1 1 0 0 0 22 16V8a1 1 0 0 0-.47-.85M15 15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1Zm5-.62l-3-1.5v-1.76l3-1.5Z"
                              />
                            </svg>
                          </button>
                        </div>

                        <div className="md:hidden mt-2 flex items-center justify-end">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleCallClick(agent, false)}
                              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-150"
                              style={{
                                backgroundColor: '#e3f2fd',
                                color: '#1e88e5',
                              }}
                              title={t('Voice Call')}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="white"
                              >
                                <path
                                  fill="currentColor"
                                  d="M16.57 22a2 2 0 0 0 1.43-.59l2.71-2.71a1 1 0 0 0 0-1.41l-4-4a1 1 0 0 0-1.41 0l-1.6 1.59a7.55 7.55 0 0 1-3-1.59a7.6 7.6 0 0 1-1.59-3l1.59-1.6a1 1 0 0 0 0-1.41l-4-4a1 1 0 0 0-1.41 0L2.59 6A2 2 0 0 0 2 7.43A15.28 15.28 0 0 0 6.3 17.7A15.28 15.28 0 0 0 16.57 22M6 5.41L8.59 8L7.3 9.29a1 1 0 0 0-.3.91a10.1 10.1 0 0 0 2.3 4.5a10.1 10.1 0 0 0 4.5 2.3a1 1 0 0 0 .91-.27L16 15.41L18.59 18l-2 2a13.28 13.28 0 0 1-8.87-3.71A13.28 13.28 0 0 1 4 7.41zM20 11h2a8.81 8.81 0 0 0-9-9v2a6.77 6.77 0 0 1 7 7"
                                />
                                <path
                                  fill="currentColor"
                                  d="M13 8c2.1 0 3 .9 3 3h2c0-3.22-1.78-5-5-5z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => enableVideoCall && handleCallClick(agent, true)}
                              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-150"
                              style={{
                                backgroundColor: enableVideoCall ? '#e8f5e9' : '#f5f5f5',
                                color: enableVideoCall ? '#4caf50' : '#9e9e9e',
                              }}
                              title={t('Video Call')}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="white"
                              >
                                <path
                                  fill="currentColor"
                                  d="M21.53 7.15a1 1 0 0 0-1 0L17 8.89A3 3 0 0 0 14 6H5a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h9a3 3 0 0 0 3-2.89l3.56 1.78A1 1 0 0 0 21 17a1 1 0 0 0 .53-.15A1 1 0 0 0 22 16V8a1 1 0 0 0-.47-.85M15 15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1Zm5-.62l-3-1.5v-1.76l3-1.5Z"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => handleConfirmCall()}
        title={t('Call Agent')}
        type="info"
        confirmText={isSystemReady ? t('Confirm') : t('Network optimizing...')}
        confirmDisabled={!isSystemReady}
        cancelText="Cancel"
        message={
          <>
            {t('Are you sure you want to call')} <strong>{selectedAgent?.name}</strong>?<br />
            {t('SIP Account')}:{' '}
            {`sip:${selectedAgent?.sipAccount}@${selectedAgent?.sipAccountInfo?.domain ?? 'im.qrtalkie.com'}`}
          </>
        }
      />

      <CallModal
        ref={{ remoteVideoRef, localVideoRef }}
        isOpen={isCallModalOpen}
        onClose={handleHangup}
        onToggleVideo={handleToggleVideo}
        callStatus={callStatus}
        isVideoCall={isVideoCall}
        isLocalVideoActive={isLocalVideoActive}
        roomName={selectedAgent?.name}
        avatarUrl={selectedAgent?.avatar}
        onOpenStats={() => {}}
      />

      <audio id="remoteAudio" autoPlay />
    </div>
  );
};

export default CallAgentVisitPage;
